/**
 * resqme — lazy image loader (server-emitted data-src only)
 *
 * Images rendered through `snippets/image.liquid` (default branch) come
 * from the server already deferred:
 *
 *   <img data-src="https://..." data-srcset="..." src="data:image/gif..." data-lazy="1">
 *
 * The real URL sits in data-src; the browser's preload scanner never
 * sees a real network URL, so no wasted requests. This script picks up
 * those <img data-src> elements and decides per-image:
 *
 *   • If the image is in an LCP-critical section (header / hero) OR
 *     already in/near the viewport → copy data-src into src IMMEDIATELY
 *     (safety net for opt-out misses + no flash for above-the-fold).
 *
 *   • Otherwise → observe with IntersectionObserver (200px rootMargin)
 *     and copy data-src into src when the image scrolls within 200px
 *     of the viewport.
 *
 * Images without `data-src` are ignored — they're either eager-rendered
 * by the snippet (`eager: true`) or come from a section / snippet that
 * hasn't been migrated to the new pattern yet. Both load normally.
 *
 * Per-image opt-out: add `data-no-lazy` to any <img> with data-src to
 * force an immediate load (skip the IntersectionObserver).
 */
(function () {
  if (!('IntersectionObserver' in window)) return;

  // Sections whose images should LOAD IMMEDIATELY (LCP-critical: header
  // navigation and hero banner). Acts as a safety net in case any
  // caller forgot to pass `eager: true` for an image in these sections.
  var EXCLUDE_SELECTORS = [
    'header',
    'header-component',
    '.header-section',
    '#header-group',
    '.hero-wrapper',
    '.hero__media',
  ];

  // Margin around the viewport at which IO fires. 200px ahead of the
  // viewport gives most images enough time to load before the user can
  // actually see them.
  var ROOT_MARGIN = '200px';

  /** @param {Element} img */
  function isExcluded(img) {
    if (!img.closest) return false;
    for (var i = 0; i < EXCLUDE_SELECTORS.length; i++) {
      var sel = EXCLUDE_SELECTORS[i];
      if (sel && img.closest(sel)) return true;
    }
    return false;
  }

  /** @param {Element} el */
  function isInOrNearViewport(el) {
    var rect = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    // Mirror ROOT_MARGIN — if the image is already within the IO trigger
    // zone, just load it now (the IO would fire immediately anyway).
    return rect.top < vh + 200 && rect.bottom > -200;
  }

  /** @param {Element} img */
  function loadImage(img) {
    var src = img.getAttribute('data-src');
    var srcset = img.getAttribute('data-srcset');
    if (src) img.setAttribute('src', src);
    if (srcset) img.setAttribute('srcset', srcset);
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-lazy');
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        // console.log('Loading lazy image:', 'data-src="' + entry.target.getAttribute('data-src') + '"');
        loadImage(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, { rootMargin: ROOT_MARGIN });

  /** @param {Element} img */
  function prepareImage(img) {
    // Explicit opt-out — load immediately, never observe.
    if (img.hasAttribute('data-no-lazy')) {
      loadImage(img);
      return;
    }
    // Inside <picture> — skip (preserves art-direction breakpoints;
    // <picture> is only used for the hero anyway, which is excluded).
    if (img.parentElement && img.parentElement.tagName === 'PICTURE') return;

    // LCP-critical section OR already in viewport — load NOW so the
    // user doesn't see a blank flash. Otherwise observe.
    if (isExcluded(img) || isInOrNearViewport(img)) {
      loadImage(img);
    } else {
      observer.observe(img);
    }
  }

  // ── Video deferral ───────────────────────────────────────────────
  // <video[data-lazy-video]> elements have their <source> URLs sitting
  // in `data-src` (not `src`) and a `preload="none"` attribute so the
  // browser doesn't fetch any video bytes on initial paint. When the
  // video scrolls within 200px of the viewport we promote each source
  // (data-src → src), restore the default preload behavior, call
  // .load() to refresh the media element, and trigger .play() so the
  // existing `autoplay` attribute takes effect.

  /**
   * Promote a lazy <video> element's <source data-src> URLs to real src,
   * refresh the media element, and trigger autoplay if the attr is set.
   * @param {HTMLVideoElement} video
   */
  function loadVideo(video) {
    if (!video || video.dataset.lazyLoaded === 'true') return;
    // Promote each <source data-src> → src.
    video.querySelectorAll('source[data-src]').forEach(function (s) {
      var url = s.getAttribute('data-src');
      if (url) s.setAttribute('src', url);
      s.removeAttribute('data-src');
    });
    // Restore the poster image (Shopify's video_tag auto-generates one;
    // we deferred it via data-poster so the browser wouldn't fetch the
    // poster JPG on initial paint).
    var posterUrl = video.getAttribute('data-poster');
    if (posterUrl) {
      video.setAttribute('poster', posterUrl);
      video.removeAttribute('data-poster');
    }
    video.dataset.lazyLoaded = 'true';
    video.removeAttribute('data-lazy-video');
    video.removeAttribute('preload');
    try { video.load(); } catch (e) {}
    if (video.autoplay) {
      try {
        var p = video.play();
        if (p && typeof p.catch === 'function') {
          p.catch(function () {/* autoplay blocked by browser — ignored */});
        }
      } catch (e) {}
    }
  }

  var videoObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        console.log('Loading lazy video');
        if (entry.target instanceof HTMLVideoElement) {
          loadVideo(entry.target);
        }
        videoObserver.unobserve(entry.target);
      }
    });
  }, { rootMargin: ROOT_MARGIN });

  /**
   * @param {HTMLVideoElement} video
   */
  function prepareVideo(video) {
    if (video.dataset.lazyLoaded === 'true') return;
    if (video.hasAttribute('data-no-lazy')) {
      loadVideo(video);
      return;
    }
    // LCP-critical OR already visible — load now, no observer.
    if (isExcluded(video) || isInOrNearViewport(video)) {
      loadVideo(video);
    } else {
      videoObserver.observe(video);
    }
  }

  function processAll() {
    // Images with data-src (server-emitted).
    document.querySelectorAll('img[data-src]').forEach(function (img) {
      prepareImage(img);
    });
    // Videos with data-lazy-video (server-emitted, sources in data-src).
    document.querySelectorAll('video[data-lazy-video]').forEach(function (v) {
      if (v instanceof HTMLVideoElement) prepareVideo(v);
    });
  }

  // ── Dynamic-content support ─────────────────────────────────────
  // Many components inject HTML into the DOM AFTER the initial
  // processAll() scan — product-recommendations (fetches from
  // /recommendations/products), cart drawer morphs, search results,
  // variant swaps that re-render product media, etc. Those new
  // <img data-src> / <video data-lazy-video> elements would never
  // be observed, so their data-src would stay un-promoted and the
  // images/videos would silently never load. A MutationObserver
  // on document.body catches every added subtree and runs the same
  // prepareImage/prepareVideo pipeline on the new elements.

  /**
   * Scan an added DOM subtree for lazy <img> and <video> elements
   * and pipe them through prepareImage / prepareVideo.
   * @param {Node} root
   */
  function processNewContent(root) {
    if (!root || root.nodeType !== 1) return; // ELEMENT_NODE only
    var el = /** @type {Element} */ (root);
    // The root itself might be a lazy image/video.
    if (el.matches && el.matches('img[data-src]')) {
      prepareImage(el);
    } else if (el.matches && el.matches('video[data-lazy-video]') && el instanceof HTMLVideoElement) {
      prepareVideo(el);
    }
    // Then scan descendants.
    if (el.querySelectorAll) {
      el.querySelectorAll('img[data-src]').forEach(function (img) { prepareImage(img); });
      el.querySelectorAll('video[data-lazy-video]').forEach(function (v) {
        if (v instanceof HTMLVideoElement) prepareVideo(v);
      });
    }
  }

  function startDynamicObserver() {
    if (!document.body || typeof MutationObserver === 'undefined') return;
    var contentObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          processNewContent(node);
        });
      });
    });
    contentObserver.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    processAll();
    startDynamicObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
